import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import Modal from 'react-native-modal';
import { SafeAreaView } from 'react-native-safe-area-context';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PtfKeyboardAvoidingView from '../components/PtfKeyboardAvoidingView';

import PantryDetails from '../components/pantry/PantryDetails';
import PantryFilter from '../components/pantry/PantryFilter';
import PantryList from '../components/pantry/PantryList';
import PantryMap from '../components/pantry/PantryMap';
import PantrySearchBar from '../components/pantry/PantrySearchBar';
import PantrySpinner from '../components/pantry/PantrySpinner';
import Colors from '../constants/Colors';
import Layout from '../constants/Layout';
import TopMenu from '../navigation/TopMenu';
import * as Actions from '../store/Actions';
import { useLocalization } from '../store/utils/useLocalization';
import { getToken } from '../utils/token';

function PantriesScreen(props) {
  const { navigation, registration, actions, route, pantry } = props;
  const { locale, translation } = useLocalization();
  const [updatedAt, setUpdatedAt] = useState(0);
  const [selectedPantryId, setSelectedPantryId] = useState(null);
  const [tabView, setTabView] = useState('map');
  const [searchTerm, setSearchTerm] = useState('');
  const { width, height: windowHeight } = useWindowDimensions();
  const selectedPantryDetailsLoaded = selectedPantryId === pantry.pantry?.id; // check if the redux pantry details matches the selected pantry

  // NOTE: Auto-switching to list view disabled - it was stealing focus while typing
  // Users can manually tap the List tab to see search results
  // This provides better UX and doesn't interrupt the search experience

  const labels = {
    map: translation.fragment_main_tabitem_pantrymap,
    list: translation.fragment_main_tabitem_pantrylist,
  };
  const showFilter = Boolean(route.params?.showFilter);

  // Handle search changes with backend search integration
  const handleSearchChange = async (newSearchTerm) => {
    setSearchTerm(newSearchTerm);

    const token = await getToken();
    const isLoggedIn = Boolean(token);

    if (newSearchTerm.trim().length >= 3) {
      // Trigger backend search for queries with 3+ characters
      actions.searchPantries(newSearchTerm.trim(), {}, isLoggedIn);
    } else if (newSearchTerm.trim().length === 0) {
      // Clear search and return to explore mode
      actions.clearSearch();
    }
    // For 1-2 characters, show current pantries without search
  };

  // Determine data source based on Redux state
  const isSearchMode = pantry.displayMode === 'search';
  const dataSource = isSearchMode ? pantry.searchResults || [] : pantry.pantries || [];

  // Create single search bar instance to share between map and list views
  const searchBar = (
    <PantrySearchBar translation={translation} searchTerm={searchTerm} onSearchChange={handleSearchChange} />
  );

  useEffect(() => {
    async function getData() {
      const token = await getToken();
      actions.getVisits();
      actions.getTefapConfig();
    }
    getData();

    navigation.addListener('state', (navState) => {
      const newId = get(navState, 'data.state.routes.0.params.id');
      if (newId) {
        // Plentiful IDs are numeric; Lighthouse IDs are UUID strings — only
        // coerce when we know it's a pure-digit value, otherwise the UUID
        // gets truncated and selectedPantryId never matches the detail row.
        const normalizedId = /^\d+$/.test(String(newId)) ? parseInt(newId, 10) : newId;
        if (normalizedId !== selectedPantryId) {
          setSelectedPantryId(normalizedId);
        }
      }
    });
  }, []);

  useEffect(() => {
    async function getSinglePantry() {
      const token = await getToken();
      const isLoggedIn = Boolean(token);
      if (route.params && route.params.id) {
        const routeId = route.params.id;
        const normalizedId = /^\d+$/.test(String(routeId)) ? parseInt(routeId, 10) : routeId;
        setSelectedPantryId(normalizedId);
        actions.showSinglePantry(routeId, isLoggedIn);
      }
    }
    getSinglePantry();
  }, [route.params?.id]); // Only depend on route.params.id

  const selectPantry = async (pantryId) => {
    setSelectedPantryId(pantryId);
    const token = await getToken();
    const isLoggedIn = Boolean(token);

    actions.showSinglePantry(pantryId, isLoggedIn);
  };

  const getFilterView = () => (
    <PantryFilter pantry={pantry} actions={actions} translation={translation} navigation={navigation} />
  );
  const getMapView = () => (
    <PantryMap
      actions={actions}
      locale={locale}
      navigation={navigation}
      registration={registration}
      selectPantry={selectPantry}
      selectedPantryId={selectedPantryId}
      setSelectedPantryId={setSelectedPantryId}
      targetZipCode={route.params?.zip}
      translation={translation}
      updatedAt={updatedAt}
      searchBar={searchBar}
      dataSource={dataSource}
    />
  );
  const getMapWithSearchView = () => (
    <View style={styles.mapWithSearchContainer}>
      {!showFilter && searchBar}
      {getMapView()}
    </View>
  );
  const getPantryListView = () => (
    <PantryList
      translation={translation}
      locale={locale}
      pantry={pantry}
      actions={actions}
      navigation={navigation}
      updatedAt={updatedAt}
      selectPantry={selectPantry}
      searchBar={searchBar}
      dataSource={dataSource}
    />
  );

  const goBack = () => {
    setSelectedPantryId(null);
  };

  const navigateToProfile = () => {
    actions.closeMustVerify();
    setSelectedPantryId(null);
    actions.getCurrentUser(() => {
      navigation.navigate('a', { screen: 'profile', params: { screen: 'info' } });
    });
  };

  let content = null;
  if (Layout.usePantryTabs) {
    content = (
      <View style={styles.columnContainer}>
        {!showFilter && (
          <View style={styles.tabBar}>
            <View style={styles.container}>
              <TouchableOpacity
                style={tabView == 'map' ? styles.activeTab : styles.tab}
                onPress={() => setTabView('map')}
              >
                <Text style={tabView == 'map' ? styles.activeTabLabel : styles.tabLabel}>{labels.map}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={tabView == 'list' ? styles.activeTab : styles.tab}
                onPress={() => setTabView('list')}
              >
                <Text style={tabView == 'list' ? styles.activeTabLabel : styles.tabLabel}>{labels.list}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
        {!showFilter && searchBar}
        <View style={styles.container}>
          {showFilter ? (
            getFilterView()
          ) : (
            <>
              <View
                testID="map-container"
                style={[styles.viewContainer, { display: tabView === 'map' ? 'flex' : 'none' }]}
              >
                {getMapView()}
              </View>
              <View
                testID="list-container"
                style={[styles.viewContainer, { display: tabView === 'list' ? 'flex' : 'none' }]}
              >
                {getPantryListView()}
              </View>
            </>
          )}
        </View>
      </View>
    );
  } else {
    content = (
      <View style={styles.container}>
        {showFilter ? getFilterView() : getMapWithSearchView()}
        <View style={styles.listPane}>{getPantryListView()}</View>
        {selectedPantryId && (
          <Modal
            key={`pantry-details-modal`}
            isVisible
            animationOut="slideOutRight"
            animationIn="slideInRight"
            style={[
              styles.modal,
              {
                left: width / 2,
                width: Layout.usePantryTabs ? width : width / 2,
              },
            ]}
            hasBackdrop={false}
            coverScreen={false}
          >
            {!selectedPantryDetailsLoaded ? (
              <PantrySpinner translation={translation} showHeader={false} />
            ) : (
              <PantryDetails
                key={`pantry-details-${selectedPantryId}`}
                pantry={pantry}
                registration={registration}
                translation={translation}
                locale={locale}
                actions={actions}
                navigation={navigation}
                navigateToProfile={navigateToProfile}
                goBack={goBack}
                organizationId={selectedPantryId}
              />
            )}
          </Modal>
        )}
      </View>
    );
  }

  // Prevent Google Maps' non-React-Native divs from overflowing #root on web.
  // '100vh' is more reliable than a JS pixel value from useWindowDimensions here.
  const webScreenMaxHeight = Platform.OS === 'web' ? { maxHeight: '100vh' } : null;

  // On wide web viewports, the pantry list FlatList was expanding to full content height and forcing
  // the map to match, making zoom controls inaccessible. Applying maxHeight constrains the flex
  // container so the map stays fixed-height and the list scrolls independently.
  const keyboardAvoidingViewStyle = !Layout.usePantryTabs
    ? { flex: 1, maxHeight: windowHeight - Layout.topMenuHeight }
    : { flex: 1 };

  return (
    <SafeAreaView style={[styles.view, webScreenMaxHeight]} edges={['top', 'left', 'right']}>
      <TopMenu {...props} page="pantries" translation={translation} />
      <PtfKeyboardAvoidingView style={keyboardAvoidingViewStyle} keyboardVerticalOffset={0}>
        {content}
      </PtfKeyboardAvoidingView>
    </SafeAreaView>
  );
}

PantriesScreen.navigationOptions = {
  header: null,
};

// had to play with margin bottom to get map to show ok
const styles = StyleSheet.create({
  view: {
    color: Colors.primaryColor,
    backgroundColor: Colors.white,
    flex: 1,
    overflow: 'hidden',
    zIndex: 1,
  },
  modal: {
    top: 0,
    right: 0,
    bottom: 0,
    margin: 0,
  },
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  columnContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  viewContainer: {
    flex: 1,
  },
  mapWithSearchContainer: {
    flex: 1,
    flexDirection: 'column',
  },
  // Side-by-side (web standalone) list pane. flex 0.6 against the map's flex 1
  // gives the list 0.6/1.6 = 37.5% of the row — 75% of its former 50% share —
  // so more of the map is visible. Does not affect the mobile tab layout.
  listPane: {
    flex: 0.6,
  },
  tabBar: {
    backgroundColor: Colors.white,
    height: 48,
    width: '100%',
  },
  tab: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  activeTab: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderBottomColor: Colors.primaryColor,
    borderBottomWidth: 6,
  },
  tabLabel: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: Colors.grayText,
  },
  activeTabLabel: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: Colors.primaryColor,
  },
});

PantriesScreen.propTypes = {
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  registration: PropTypes.objectOf(PropTypes.any).isRequired,
  pantry: PropTypes.objectOf(PropTypes.any).isRequired,
  route: PropTypes.objectOf(PropTypes.any),
  navigation: PropTypes.objectOf(PropTypes.any),
};

function mapStateToProps(state) {
  return {
    registration: state.registration,
    pantry: state.pantry,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PantriesScreen);
